import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import {
    VetRuleOutcomeStepEnum,
    VetStatusEnum, VetTaskClientModel, CustomerDecisionStatusEnum, VettingType
} from "../../../../../../../../models/vetting/vetting.models";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../../../configuration/rightshipqi-router.constants";
import { LookupValue, LookupValueRequest } from "../../../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../../../../services/lookups/lookup-client.service";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import * as Enumerable from "linq";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ManageVesselService } from "../../../../../../../../services/vessels/manage-vessel.service";
import { AutoScreeningService } from "../../../../../../../../services/vetting/auto-screening.service";
import { BaseVetOverview } from "../../../../../vetting/steps/overview/base-vet-overview";
import { VettingService } from "../../../../../../../../services/vetting/vetting.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../../../client-validation-system/validations.models";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../../../client-validation-system/validations-service";
import { VesselDetailModel } from "../../../../../../../../models/vessels/vessel.models";
import { UserClientService } from "../../../../../../../../services/authentication/user-client.service";
import { CustomerSubscriptionService } from "../../../../../../../../services/modules/customer-subscription-client.service";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { CustomerVesselService } from "../../../../../../../../services/vessels/customer-vessels.service";

@autoinject
@transient()
@useView('views/pages/modules/auto-screening/templates/trade-screening/tabs/overview/trade-screening-overview.html')
export class TradeScreeningOverview extends BaseVetOverview {

    private vetStatuses: LookupValue[];
    public canProcessScreeningCustomerDecisionForSubscriptions: boolean;
    public canProcessScreeningCustomerDecisionForRightStore: boolean;
    public unacceptable: VetStatusEnum = VetStatusEnum.AS_Failed;
    public canLinkAutoScreeningWithFullVet: boolean;
    public canViewRiskScoresInScreeningOverview: boolean;
    public riskScoreSeperatorShowing: boolean;
    validator: ClientSideValidator;

    async setCustomerDecision(customerDecisionStatusId: number) {
        this.setValidator();
        const valid = await this.validator.validate();
        if (valid.valid) {
            try {
                const res = await this.vettingClientService.setCustomerDecision(this.item.vetId, customerDecisionStatusId, this.decisionComments)
                this.item.customerDecisionComment =res.comments;
                this.item.customerDecisionStatusDate= res.date;
                this.item.customerDecisionStatusBy = res.username;
                this.item.customerDecisionStatusId = customerDecisionStatusId;
                this.notificationService.show("Customer Decision", "Successfully saved", NotificationType.Success);
            } catch {
                this.notificationService.show("Customer Decision", "Failed to set customer decision", NotificationType.Error);
            }
        }
    }
    
    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item),
        Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks),
        Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length),
        Reflection.getPropertyPath<TradeScreeningOverview>(z => z.selectedOutcomes),  
        Reflection.getPropertyPath<TradeScreeningOverview>(z => z.selectedOutcomes.length))
    get vetTasks() {
        const tasks = {};
        if (this.item) {
            const filter = this.selectedOutcomes.length > 0;
            let vetTasks = this.item.vetTasks;
            const outcomeIds = this.selectedOutcomes
            if (filter) {
                const pending = outcomeIds.filter(x => x == -1).length > 0;
                const failed = outcomeIds.filter(x => x != VetRuleOutcomeStepEnum.Pass).length > 0;
                vetTasks = vetTasks.filter(task => (pending && (!task.vetTaskOutcomeId || task.vetTaskOutcomeId <= 0)) || (failed && task.systemError) || (failed && task.vetTaskOutcomeId == VetRuleOutcomeStepEnum.NA) || outcomeIds.indexOf(task.vetTaskOutcomeId) > -1)
            }
            const items = Enumerable.from(vetTasks).groupBy(x => x.isCritical ? "Critical rules" : "Soft rules")
                .toArray();
            for (const item of items) {
                tasks[item.key()] = item.where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule).toArray();
            }
        }
        return tasks;
    }

    get vetTasksForPrint() {
        const tasks = {};
        if (this.item) {
            const vetTasks = this.item.vetTasks;
            const items = Enumerable.from(vetTasks).groupBy(x => x.isCritical ? "Critical rules" : "Soft rules")
                .toArray();
            for (const item of items) {
                tasks[item.key()] = item.where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule).toArray();
            }
        }
        return tasks;
    }

    get passed() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.Pass && !x.systemError).length;
        }
        return 0;
    }

    get failed() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.Fail || x.systemError).length;
        }
        return 0;
    }

    get na() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.NA).length;
        }
        return 0;
    }

    shouldShowSafetyScoreAndGhg(statusId: number) {
        return statusId == VetStatusEnum.AS_Passed ||
            statusId == VetStatusEnum.AS_ExceptionRequired ||
            statusId == VetStatusEnum.AS_Failed;
    }

    inspectionAsOf(): Date[] {
        return this.lastVetStatusHistories.map(x => this.completed(x.statusId) ? x.createdDate : null);
    }


    completed(statusId: number) {
        return statusId == VetStatusEnum.AS_Passed ||
            statusId == VetStatusEnum.AS_Failed;
    }

    @computedFrom("item", "item.vetStatusId")
    get showCustomerDecision() {
        return this.item && this.item.vetStatusId == VetStatusEnum.AS_ExceptionRequired;
    }

    @computedFrom("item", "item.vetStatusId", "item.customerDecisionStatusId")
    get customerDecisionStatus() {
        if (this.item) {
            if (!this.item.requireCustomerDecision && !this.showCustomerDecision) return "";

            if (this.item.customerDecisionStatusId) {
                return this.getDecisionStatus(this.item.customerDecisionStatusId);
            }
        }
        return "Pending";
    }

    @computedFrom("item", "item.vetStatusId", "item.previousCustomerDecisionStatusId")
    get previousCustomerDecisionStatus() {
        if (this.item && this.item.previousCustomerDecisionStatusId) {
            return this.getDecisionStatus(this.item.previousCustomerDecisionStatusId);
        }
        return "";
    }

    getDecisionStatus(customerDecisionStatusId: number) {
        if (customerDecisionStatusId) {
            if (customerDecisionStatusId == CustomerDecisionStatusEnum.Acceptable) {
                return "Exception Granted";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Unacceptable) {
                return "Exception Rejected";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Pending) {
                return "Pending";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Cancelled) {
                return "Cancelled";
            }
        }
    }
    
    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))
    get anyCriticalFailed() {
        return this.item.vetTasks.filter(x => x.isCritical && (x.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || x.systemError)).length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))   
    get failedCriticalCount() {
        return this.item.vetTasks.filter(x => x.isCritical && (x.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || x.systemError)).length;
    }

    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))   
    get criticalCount() {
        return this.item.vetTasks.filter(x => x.isCritical).length;
    }

    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))  
    get anySoftFailed() {
        return this.item.vetTasks.filter(x => !x.isCritical && (x.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || x.systemError)).length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))  
    get failedSoftCount() {
        return this.item.vetTasks.filter(x => !x.isCritical && (x.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || x.systemError)).length;
    }

    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length)) 
    get softCount() {
        return this.item.vetTasks.filter(x => !x.isCritical).length;
    } 

    get vetStatus() {
        if (this.vetStatuses && this.item)
            return this.vetStatuses.filter(x => x.id == this.item.vetStatusId)[0];

        return null;
    }

    get lastVetStatusHistoryUnacceptable() {
        return this.lastVetStatusHistories.filter(x => x.statusId == VetStatusEnum.AS_Failed)[0];
    }

    get lastVetStatusHistoryEscalated() {
        return this.lastVetStatusHistories.filter(x => x.statusId == VetStatusEnum.AS_ExceptionRequired)[0];
    }

    @computedFrom("item", "item.vetStatusHistory", "item.vetStatusHistory.length", "item.vetStatusId")
    get lastVetStatusHistories() {
        const list = [this.item.vetStatusHistory[0]];

        if (this.showCustomerDecision && this.item.vetStatusHistory.length > 1 && this.item.vetStatusHistory[1].statusId == VetStatusEnum.AS_Failed)
            list.push(this.item.vetStatusHistory[1]);

        return list;
    }
        
    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))
    get vetTaskKeys() {
        if (this.item)
            return Enumerable.from(this.item.vetTasks).where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule)
                .orderBy(x => x.categoryIsCustomer ? 1 : 2).select(x => x.isCritical ? "Critical rules" : "Soft rules").distinct().toArray();
        return [];
    }

    @computedFrom("item", "item.isRightStoreVet", "canProcessScreeningCustomerDecisionForSubscriptions", "canProcessScreeningCustomerDecisionForRightStore")
    get canProcessAutoScreeningCustomerDecision()
    {
        if(!this.item) return false;
        return this.item.isRightStoreVet? this.canProcessScreeningCustomerDecisionForRightStore: this.canProcessScreeningCustomerDecisionForSubscriptions;
    }

    constructor(private vettingClientService: AutoScreeningService, signalClientService: SignalClientService, manageVesselService: ManageVesselService,
        notificationsService: NotificationClientService, lookupClientService: LookupClientService, vettingService: VettingService,
        userClaimsService: UserClaimsService, dialogClientService: DialogClientService, private entityValidationsService: EntityValidationsService,
        i18N: I18N, router: Router, userClientService: UserClientService, configService: ConfigService,
        customerSubscriptionService: CustomerSubscriptionService,
        customerVesselsService: CustomerVesselService
    ) {
        super(notificationsService, i18N, vettingService, dialogClientService, userClaimsService, signalClientService, 
            manageVesselService, router, userClientService,
            customerSubscriptionService, customerVesselsService, configService);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        request.parameters = "1,2";
        lookupClientService.getLookup(request).then(x => {
            this.vetStatuses = x.activeValues;
        });
        this.canProcessScreeningCustomerDecisionForSubscriptions = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForSubscriptions);
        this.canProcessScreeningCustomerDecisionForRightStore = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForSubscriptions);
        this.canLinkAutoScreeningWithFullVet = this.userClaimsService.hasPermission(Claims.CanLinkAutoScreeningWithFullVet);
        this.canViewRiskScoresInScreeningOverview = this.userClaimsService.hasPermission(Claims.CanViewRiskScoresInScreeningOverview);
        this.isAutoScreen = true;

        //Select "Failed" rules by default - DEL-5705
        this.selectedOutcomes.push(VetRuleOutcomeStepEnum.Fail);
    }

    protected onActivated() {
        this.setCategories();
    }

    protected onPermissionsChanged = (userResponse) =>
    {
        return new Promise<void>((resolve) => {
            if(userResponse && userResponse.allowedClaims)
            {
                this.userClaimsService.setUserClaims(userResponse.allowedClaims);
            }
            this.canProcessScreeningCustomerDecisionForSubscriptions = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForSubscriptions);
            this.canProcessScreeningCustomerDecisionForRightStore = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForSubscriptions);
            this.canLinkAutoScreeningWithFullVet = this.userClaimsService.hasPermission(Claims.CanLinkAutoScreeningWithFullVet);
            this.canViewRiskScoresInScreeningOverview = this.userClaimsService.hasPermission(Claims.CanViewRiskScoresInScreeningOverview);

            resolve();
        });
    }

    filterClick(outcome: { id: number; name: string }) {
        const index = this.selectedOutcomes.indexOf(outcome.id);
        if (index >= 0) {
            this.selectedOutcomes.splice(index, 1);
        }
        else {
            this.selectedOutcomes.push(outcome.id);
        }
    }

    navigateToFullVet() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Vet, { id: this.item.fullVetId });
    }

    private getOutcome(x: VetTaskClientModel) {
        return x.vetSuperVetTaskOutcomeId ? x.vetSuperVetTaskOutcomeId : x.vetTaskOutcomeId;
    }

    selectedOutcomes: number[] = [];
    categories: { id: number; name: string }[] = [];
    
    @computedFrom(Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks), Reflection.getPropertyPath<TradeScreeningOverview>(z => z.item.vetTasks.length))
    get outcomes(): { id: number; name: string }[] {
        const list = [];
        if (this.item && this.item.vetTasks) {
            const vetTasks: VetTaskClientModel[] = this.item.vetTasks;
            const passed = vetTasks.filter(task => task.vetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass).length;
            const failed = vetTasks.filter(task => task.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || task.systemError).length;

            list.push({ id: VetRuleOutcomeStepEnum.Pass, name: `Passed (${passed})` });
            list.push({ id: VetRuleOutcomeStepEnum.Fail, name: `Failed (${failed})` });
        }

        return list;
    }

    setCategories() {
        const vetTasks: VetTaskClientModel[] = Enumerable.from(this.vetTasks).selectMany(x => <VetTaskClientModel[]>x.value).toArray();
        this.categories = Enumerable.from(vetTasks.map(x => {
            return {
                id: x.isManual ? -1 : x.categoryId,
                name: x.isManual ? "Manual" : x.category
            };
        })).groupBy(x => x.id).select(x => x.firstOrDefault()).where(x => x != null).toArray();
    }

    getTasks() {
        return this.item.vetTasks
            .map(x => {
                return {
                    vetTaskId: x.vetTaskId,
                    name: x.title,
                    comment: x.isInfoOnlyRule ? x.infoVettingComment
                        : x.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass ? x.passedVettingComment
                            : x.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail ? x.failedVettingComment
                                : x.naVettingComment,
                    passedComment: x.passedVettingComment,
                    failedComment: x.failedVettingComment,
                    forceIncludeComment: x.forceIncludeComment,
                    isInfoOnlyRule: x.isInfoOnlyRule,
                    infoComment: x.infoVettingComment,
                    naComment: x.naVettingComment,
                    outcomeId: x.vetSuperVetTaskOutcomeId
                };
            })
    }

    @computedFrom("lastVetStatusHistory", "lastVetStatusHistory.user")
    get lastVetStatusHistoryUsers() {
        const systemStatuses = [
            VetStatusEnum.Processing,
            VetStatusEnum.ProcessingOutcome
        ];
        const users = this.lastVetStatusHistories.map(x => {
            let user = x.user;
            if (!user && (systemStatuses.indexOf(x.statusId) > -1
                || (x.isAutoCancelled == true))) user = "System";
            return user;
        });
        return users;
    }

    openContactDetailsDialog() {
        this.item.initialEmailsForCorrespondence = [{ email: this.item.requestedByEmail, recipientType: "Requester" }]

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ContactDetailsDialog, {
            model: this.item,
            onConfirm: null
        }, true, false, false);
    }

    toggle(element: HTMLElement, arrow: HTMLElement) {
        $(element).slideToggle()
        if ($(arrow).hasClass('is-opened')) {
            $(arrow).removeClass('is-opened');
        } else {
            $(arrow).addClass('is-opened');
        }
    }

    requestAFullVet() {
        const linkFullVetitem = CloneHelper.cloneObject(this.item);
        // set cloned autoscreen vetId to 0 so that we create a new one from autoscreen
        linkFullVetitem.vetId = 0;
        // init these two properties as false because they does not exist in autoscreens
        linkFullVetitem.vetForSTS = false;
        linkFullVetitem.charterTypeId = null;
        this.manageVesselService.showChooseDialog(
            {
                vessel: <VesselDetailModel>{
                    vetRequiresMoreInformationEmails: this.vessel.vetRequiresMoreInformationEmails,
                    documentOfComplianceEmail: this.vessel.documentOfComplianceEmail,
                    cargoTypeCategoryId: this.vessel.cargoTypeCategoryId,
                    vesselId: this.vessel.vesselId,
                    internationalMaritimeOrganizationNumber: this.vessel.internationalMaritimeOrganizationNumber,
                    name: this.vessel.name
                },
                vettingType: VettingType.FullVet,
                parentVetId: this.item.vetId,
                vet: linkFullVetitem,
                linkFullVetCheck: true

            });
    }

    private setValidator() {
        const rules = [{
            errorMessage: "Comments are required",
            parameters: null, 
            property: "decisionComments",
            validationType: ValidationTypeEnum.Required
        }];

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Customer Decision Outcome",
            isCollection: false,
            nestedRulesHolder: [],
            rules: rules
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    //Show seperator the first time its not "Critical" or "Soft" rule within the loop
    showSeperatorForRiskScores(risk: string) {
        if(risk == "Critical") {
            this.riskScoreSeperatorShowing = true;
        }
        else if(risk == "Soft") {
            this.riskScoreSeperatorShowing = true;
        }
        else if(this.riskScoreSeperatorShowing) {
            return true;
        }
    }
}
